import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, Copy, Loader2, Plus, Trash2 } from "lucide-react";
import { Link } from "wouter";
import { ManualExpenseForm } from "./ManualExpenseForm";
import {
  formatInstallationReference,
  MANUAL_EXPENSE_SCOPE_LABELS,
  MANUAL_EXPENSE_STATUS_LABELS,
  type ManualExpenseScope,
  type ManualExpenseStatus,
} from "@shared/finance/manualExpenses";

const STATUS_COLORS: Record<ManualExpenseStatus, "secondary" | "outline" | "default" | "destructive"> = {
  draft: "secondary",
  planned: "outline",
  pending_payment: "default",
  paid: "default",
  cancelled: "destructive",
};

const SOURCE_LABELS: Record<string, string> = {
  manual: "Manual",
  import: "Importado",
};

function formatAmount(val: string | number | null | undefined): string {
  if (val == null) return "-";
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString("es-ES", { style: "currency", currency: "EUR" }) : "-";
}

type PendingAction =
  | { type: "pay"; id: number; date: string }
  | { type: "cancel"; id: number; reason: string }
  | { type: "delete"; id: number }
  | { type: "duplicate"; id: number; date: string }
  | null;

export function ManualExpensesTab() {
  const [statusFilter, setStatusFilter] = useState<ManualExpenseStatus | "all">("all");
  const [scopeFilter, setScopeFilter] = useState<ManualExpenseScope | "all">("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [installationFilter, setInstallationFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const utils = trpc.useUtils();
  const todayQuery = trpc.manualExpenses.todayMadrid.useQuery();
  const categoriesQuery = trpc.financialCategories.list.useQuery({ includeInactive: false });
  const installationsQuery = trpc.installations.list.useQuery();
  const suppliersQuery = trpc.productCatalog.listSuppliers.useQuery({ active: true });

  useEffect(() => {
    setPage(1);
  }, [statusFilter, scopeFilter, categoryFilter, installationFilter, supplierFilter, from, to, search]);

  const listQuery = trpc.manualExpenses.list.useQuery({
    status: statusFilter === "all" ? undefined : statusFilter,
    scope: scopeFilter === "all" ? undefined : scopeFilter,
    categoryId: categoryFilter === "all" ? undefined : Number(categoryFilter),
    installationId: installationFilter === "all" ? undefined : Number(installationFilter),
    supplierId: supplierFilter === "all" ? undefined : Number(supplierFilter),
    from: from || undefined,
    to: to || undefined,
    search: search || undefined,
    page,
    pageSize: 50,
  });

  const invalidateList = () => utils.manualExpenses.list.invalidate();
  const planMutation = trpc.manualExpenses.plan.useMutation({ onSuccess: invalidateList });
  const pendingPaymentMutation = trpc.manualExpenses.markPendingPayment.useMutation({ onSuccess: invalidateList });
  const markPaidMutation = trpc.manualExpenses.markPaid.useMutation({ onSuccess: () => { setPendingAction(null); invalidateList(); } });
  const cancelMutation = trpc.manualExpenses.cancel.useMutation({ onSuccess: () => { setPendingAction(null); invalidateList(); } });
  const deleteMutation = trpc.manualExpenses.softDelete.useMutation({ onSuccess: () => { setPendingAction(null); invalidateList(); } });
  const duplicateMutation = trpc.manualExpenses.duplicate.useMutation({ onSuccess: () => { setPendingAction(null); invalidateList(); } });

  if (listQuery.isLoading) return <div className="flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</div>;
  if (listQuery.error) return <div className="flex items-center gap-2 py-4 text-destructive"><AlertCircle className="h-4 w-4" /> Error: {listQuery.error.message}</div>;

  const data = listQuery.data;
  const rows = (data?.rows ?? []) as any[];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / (data?.pageSize ?? 50)));
  const categories = categoriesQuery.data ?? [];
  const installations = installationsQuery.data ?? [];
  const suppliers = suppliersQuery.data ?? [];
  const today = todayQuery.data?.date ?? "";
  const unallocatedSummary = (data?.summaryByDestination as any)?.unallocated;
  const unallocatedCount = Number(unallocatedSummary?.count ?? 0);

  const mutationError = markPaidMutation.error || cancelMutation.error || deleteMutation.error || duplicateMutation.error || planMutation.error || pendingPaymentMutation.error;

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
        Usa esta sección solo cuando no dispones de factura de proveedor. Si tienes factura, regístrala en Facturas recibidas.
      </div>
      <div className="flex min-w-0 flex-col items-stretch gap-3 xl:flex-row xl:items-end">
        <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 xl:flex-1">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger className="w-full min-w-0"><SelectValue className="min-w-0 flex-1 truncate" placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(MANUAL_EXPENSE_STATUS_LABELS).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scopeFilter} onValueChange={(v) => setScopeFilter(v as any)}>
            <SelectTrigger className="w-full min-w-0"><SelectValue className="min-w-0 flex-1 truncate" placeholder="Ámbito" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(MANUAL_EXPENSE_SCOPE_LABELS).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full min-w-0"><SelectValue className="min-w-0 flex-1 truncate" placeholder="Categoría" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Categorías</SelectItem>
              {categories.map((category: any) => <SelectItem key={category.id} value={String(category.id)}>{category.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={installationFilter} onValueChange={setInstallationFilter}>
            <SelectTrigger className="w-full min-w-0"><SelectValue className="min-w-0 flex-1 truncate" placeholder="N.º expediente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Expedientes</SelectItem>
              {installations.map((installation: any) => (
                <SelectItem key={installation.id} value={String(installation.id)}>
                  {formatInstallationReference({
                    id: installation.id,
                    installationCode: installation.installationCode,
                    expedienteCode: installation.installationCode ?? null,
                    clientId: installation.clientId ?? null,
                    clientName: installation.clientName ?? null,
                    siteName: installation.siteName ?? null,
                    workDescription: installation.workDescription ?? null,
                    name: installation.workDescription ?? null,
                    address: installation.address ?? null,
                    status: installation.status ?? null,
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={supplierFilter} onValueChange={setSupplierFilter}>
            <SelectTrigger className="w-full min-w-0"><SelectValue className="min-w-0 flex-1 truncate" placeholder="Proveedor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Proveedores</SelectItem>
              {suppliers.map((supplier: any) => <SelectItem key={supplier.id} value={String(supplier.id)}>{supplier.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          <Input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar gasto o expediente" />
        </div>
        <Button className="self-start xl:self-auto" onClick={() => { setEditId(null); setShowForm(true); }} size="sm">
          <Plus className="mr-2 h-4 w-4" /> Nuevo gasto sin factura
        </Button>
      </div>

      {data?.summary && (
        <div className="space-y-2">
          {unallocatedCount > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {unallocatedCount} gasto{unallocatedCount !== 1 ? "s" : ""} pendiente{unallocatedCount !== 1 ? "s" : ""} de imputar · {formatAmount(unallocatedSummary?.totalAmount)}
              </span>
            </div>
          )}
          <div className="grid gap-2 text-sm sm:grid-cols-3">
            <div className="rounded-md border p-3"><span className="text-muted-foreground">Base</span><div className="font-semibold">{formatAmount(data.summary.taxableBase)}</div></div>
            <div className="rounded-md border p-3"><span className="text-muted-foreground">IVA</span><div className="font-semibold">{formatAmount(data.summary.vatAmount)}</div></div>
            <div className="rounded-md border p-3"><span className="text-muted-foreground">Total</span><div className="font-semibold">{formatAmount(data.summary.totalAmount)}</div></div>
          </div>
          {data.summaryByDestination && (
            <div className="grid gap-2 text-sm md:grid-cols-4">
              {Object.entries(MANUAL_EXPENSE_SCOPE_LABELS).map(([scope, label]) => (
                <div key={scope} className="rounded-md border p-3">
                  <span className="text-muted-foreground">{label}</span>
                  <div className="font-semibold">{formatAmount((data.summaryByDestination as any)[scope]?.totalAmount)}</div>
                  <div className="text-xs text-muted-foreground">
                    {Number((data.summaryByDestination as any)[scope]?.count ?? 0)} gasto{Number((data.summaryByDestination as any)[scope]?.count ?? 0) !== 1 ? "s" : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <ManualExpenseForm
          editId={editId}
          onSuccess={() => { setShowForm(false); setEditId(null); invalidateList(); }}
          onCancel={() => { setShowForm(false); setEditId(null); }}
        />
      )}

      {rows.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <p className="text-sm text-muted-foreground">Sin gastos manuales registrados</p>
            <Button variant="outline" size="sm" onClick={() => { setEditId(null); setShowForm(true); }}>
              <Plus className="mr-2 h-4 w-4" /> Registrar primer gasto sin factura
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Destino</TableHead>
                <TableHead>N.º expediente</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {row.scope === "unallocated" && <AlertCircle className="h-3.5 w-3.5 text-amber-600" />}
                      <span>{MANUAL_EXPENSE_SCOPE_LABELS[row.scope as ManualExpenseScope] ?? row.scope}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.installationReference ? (
                      <Link className="font-medium text-ods-navigation hover:underline" href={`/installations/${row.installationReference.id}`}>
                        {row.installationReference.installationCode || row.installationReference.expedienteCode
                          ? `Exp. ${row.installationReference.installationCode || row.installationReference.expedienteCode}`
                          : "Expediente sin código"}
                      </Link>
                    ) : "-"}
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate">{row.installationReference?.clientName ?? "-"}</TableCell>
                  <TableCell>{row.categoryName ?? "-"}</TableCell>
                  <TableCell>{row.supplierName ?? "-"}</TableCell>
                  <TableCell className="whitespace-nowrap">{row.expenseDate}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatAmount(row.totalAmount)}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_COLORS[row.status as ManualExpenseStatus]}>
                      {MANUAL_EXPENSE_STATUS_LABELS[row.status as ManualExpenseStatus] ?? row.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{SOURCE_LABELS[row.source] ?? row.source}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-1">
                      {["draft", "planned"].includes(row.status) && (
                        <Button variant="ghost" size="sm" onClick={() => { setEditId(row.id); setShowForm(true); }}>Editar</Button>
                      )}
                      {row.status === "draft" && row.scope !== "unallocated" && (
                        <Button variant="ghost" size="sm" onClick={() => planMutation.mutate({ id: row.id })}>Planificar</Button>
                      )}
                      {row.status === "planned" && (
                        <Button variant="ghost" size="sm" onClick={() => pendingPaymentMutation.mutate({ id: row.id })}>Pendiente pago</Button>
                      )}
                      {row.status === "pending_payment" && (
                        <Button variant="ghost" size="sm" onClick={() => setPendingAction({ type: "pay", id: row.id, date: today })}>Pagado</Button>
                      )}
                      {["draft", "planned", "pending_payment"].includes(row.status) && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setPendingAction({ type: "cancel", id: row.id, reason: "" })}>Cancelar</Button>
                      )}
                      {row.status !== "draft" && (
                        <Button variant="ghost" size="sm" onClick={() => setPendingAction({ type: "duplicate", id: row.id, date: row.expenseDate })}>
                          <Copy className="mr-1 h-3.5 w-3.5" /> Duplicar
                        </Button>
                      )}
                      {["draft", "cancelled"].includes(row.status) && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setPendingAction({ type: "delete", id: row.id })}>
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Borrar
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
        <span>{totalCount} gastos · página {page} de {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Anterior</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Siguiente</Button>
        </div>
      </div>

      <Dialog open={Boolean(pendingAction)} onOpenChange={(open) => !open && setPendingAction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingAction?.type === "pay" && "Marcar gasto pagado"}
              {pendingAction?.type === "cancel" && "Cancelar gasto"}
              {pendingAction?.type === "delete" && "Borrar borrador"}
              {pendingAction?.type === "duplicate" && "Duplicar gasto"}
            </DialogTitle>
          </DialogHeader>
          {pendingAction?.type === "pay" && (
            <div className="space-y-2">
              <Label htmlFor="actualPaymentDate">Fecha real de pago</Label>
              <Input id="actualPaymentDate" type="date" value={pendingAction.date} onChange={(event) => setPendingAction({ ...pendingAction, date: event.target.value })} />
            </div>
          )}
          {pendingAction?.type === "cancel" && (
            <div className="space-y-2">
              <Label htmlFor="cancellationReason">Motivo</Label>
              <Textarea id="cancellationReason" value={pendingAction.reason} onChange={(event) => setPendingAction({ ...pendingAction, reason: event.target.value })} rows={3} />
            </div>
          )}
          {pendingAction?.type === "duplicate" && (
            <div className="space-y-2">
              <Label htmlFor="duplicateExpenseDate">Nueva fecha</Label>
              <Input id="duplicateExpenseDate" type="date" value={pendingAction.date} onChange={(event) => setPendingAction({ ...pendingAction, date: event.target.value })} />
            </div>
          )}
          {pendingAction?.type === "delete" && <p className="text-sm text-muted-foreground">El registro quedará oculto mediante borrado lógico.</p>}
          {mutationError && <p className="text-xs text-destructive">{mutationError.message}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAction(null)}>Volver</Button>
            {pendingAction?.type === "pay" && <Button disabled={!pendingAction.date || markPaidMutation.isPending} onClick={() => markPaidMutation.mutate({ id: pendingAction.id, actualPaymentDate: pendingAction.date })}>Confirmar</Button>}
            {pendingAction?.type === "cancel" && <Button disabled={!pendingAction.reason.trim() || cancelMutation.isPending} onClick={() => cancelMutation.mutate({ id: pendingAction.id, reason: pendingAction.reason })}>Cancelar gasto</Button>}
            {pendingAction?.type === "delete" && <Button variant="destructive" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate({ id: pendingAction.id })}>Borrar</Button>}
            {pendingAction?.type === "duplicate" && <Button disabled={!pendingAction.date || duplicateMutation.isPending} onClick={() => duplicateMutation.mutate({ id: pendingAction.id, expenseDate: pendingAction.date })}>Duplicar</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
